import React, { useState } from 'react';
import styles from './LoginForm.module.css';
import useAuth from '../../hooks/useAuth';
import newIcon from './assets/Monad Logo - Default - Logo Mark.svg';
import { useNavigate } from 'react-router-dom';
import Web3 from 'web3';

const LoadingSpinner = () => (
  <svg className={styles.loadingSpinner} viewBox="0 0 50 50">
    <circle className={styles.spinnerPath} cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
  </svg>
);

const BackIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#666666">
    <path d="M560-240 320-480l240-240 56 56-184 184 184 184-56 56Z"/>
  </svg>
);

const LoginForm = ({ onClose, onWalletConnect }) => {
  const [email, setEmail] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isWalletConnecting, setIsWalletConnecting] = useState(false);
  const { signInWithEmail } = useAuth();
  const navigate = useNavigate();

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    const { success, error } = await signInWithEmail(email);
    
    if (success) {
      setShowVerification(true);
    } else {
      alert(error || 'Failed to send verification email');
    }
    
    setIsLoading(false);
  };

  const handleMetaMaskLogin = async () => {
    try {
      if (typeof window.ethereum === 'undefined') {
        alert('MetaMask is not installed. Please install it to use this feature.');
        return;
      }

      const web3 = new Web3(window.ethereum);
      setIsWalletConnecting(true);

      await window.ethereum.request({
        method: 'wallet_addEthereumChain',
        params: [{
          chainId: '0x27B7',
          chainName: 'Monad Testnet',
          rpcUrls: ['https://quick-long-glade.monad-testnet.quiknode.pro/e295db338d6d5de04640a9d08676b1c815cbe975/'],
          nativeCurrency: {
            name: 'MON',
            symbol: 'MON',
            decimals: 18,
          },
          blockExplorerUrls: ['https://testnet.monadexplorer.com'],
        }],
      });

      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const publicKey = accounts[0];

      await onWalletConnect({
        address: publicKey,
        type: 'Ethereum',
        shortAddress: publicKey.slice(0, 4) + '...' + publicKey.slice(-4)
      });

      onClose();
      navigate('/dashboard');
    } catch (err) {
      console.error('Error connecting to MetaMask wallet:', err);
      alert('Failed to connect wallet: ' + err.message);
    } finally {
      setIsWalletConnecting(false);
    }
  };

  if (showVerification) {
    return (
      <div className={styles.overlay}>
        <div className={styles.modal}>
          <div className={styles.header}>
            <button className={styles.backButton} onClick={() => setShowVerification(false)}>
              <BackIcon />
            </button>
            <h2>Check your email</h2>
            <button className={styles.closeButton} onClick={onClose}>×</button>
          </div>

          <div className={styles.verificationContent}>
            <div className={styles.emailIcon}>
              <svg viewBox="0 0 24 24" className={styles.icon}>
                <path fill="currentColor" d="M20,4H4C2.9,4,2,4.9,2,6v12c0,1.1,0.9,2,2,2h16c1.1,0,2-0.9,2-2V6C22,4.9,21.1,4,20,4z M20,8l-8,5L4,8V6l8,5l8-5V8z"/>
              </svg>
            </div>
            <p className={styles.verificationText}>
              We've sent a sign in link to<br />
              <span className={styles.emailDisplay}>{email}</span>
            </p>
            <p className={styles.verificationInstruction}>
              Click the link in the email to sign in to your account.
            </p>

            <button 
              className={styles.resendButton}
              onClick={handleEmailSubmit}
              disabled={isLoading}
            >
              Re-send email
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>Log in or sign up</h2>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleEmailSubmit} className={styles.emailForm}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            className={styles.emailInput}
            required
            disabled={isLoading}
          />
          <button type="submit" className={styles.submitButton} disabled={isLoading}>
            <svg viewBox="0 0 24 24" className={styles.arrowIcon}>
              <path d="M10 17l5-5-5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </form>

        <div className={styles.divider}>
          <span>OR</span>
        </div>

        <button 
          onClick={handleMetaMaskLogin}
          className={styles.walletButton}
          disabled={isWalletConnecting}
        >
          <img 
            src={newIcon}
            alt="Monad Logo" 
            className={styles.walletIcon}
          />
          <span>{isWalletConnecting ? 'Confirm in your wallet' : 'Monad'}</span>
          {isWalletConnecting && <LoadingSpinner />}
        </button>

        <p className={styles.terms}>
          By proceeding, you agree to our{' '}
          <a href="/terms" className={styles.link}>Terms of Service</a>
          {' & '}
          <a href="/privacy" className={styles.link}>Privacy Policy</a>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;